/**
 * Report Translation Configuration
 *
 * Maps English BigQuery field names to Spanish report labels
 * for COAC credit rating report generation
 */

// ============================================================================
// BigQuery Field Translations (English → Spanish)
// ============================================================================

export const FIELD_TRANSLATIONS: Record<string, string> = {
  // Institution fields
  'institution_id': 'ID de Institución',
  'institution_name': 'Nombre de la Institución',
  'calculation_period': 'Período de Cálculo',
  'indicator_value': 'Valor del Indicador',
  'indicator_score': 'Puntaje del Indicador',
  'indicator_id': 'ID del Indicador',
  'indicator_name': 'Nombre del Indicador',
  'indicator_category': 'Categoría del Indicador',

  // Balance sheet fields
  'activo': 'Activo',
  'pasivo': 'Pasivo',
  'patrimonio': 'Patrimonio',
  'activo_total': 'Activo Total',
  'pasivo_total': 'Pasivo Total',
  'patrimonio_total': 'Patrimonio Total',

  // Chart labels
  'Percentage (%)': 'Porcentaje (%)',
  'Growth Rate': 'Tasa de Crecimiento',
  'Amount (USD)': 'Monto (USD)',
  'Period': 'Período',
  'Value': 'Valor',
  'Score': 'Puntaje',

  // Section headers
  'Balance Structure': 'Estructura de Balance',
  'Financial Indicators': 'Indicadores Financieros',
  'Solvency': 'Solvencia',
  'Liquidity': 'Liquidez',
  'Asset Quality': 'Calidad de Activos',
  'Efficiency': 'Eficiencia y Gestión',
  'Profitability': 'Rentabilidad',
  'Interest Rate Risk': 'Riesgo de Tasa de Interés',
};

// ============================================================================
// Financial Indicator Names (English → Spanish)
// ============================================================================

export const INDICATOR_NAMES_ES: Record<string, string> = {
  // Capital/Solvency (S component - Solvencia)
  'S1': 'Índice de Capitalización',
  'S2': 'Solvencia Patrimonial',
  'S3': 'Patrimonio sobre Depósitos',
  'S4': 'Cartera Bruta / Patrimonio Normalizado',
  'S5': 'Cartera Improductiva / Patrimonio',
  'S6': 'Cartera Vencida / Patrimonio',
  'S7': 'Provisiones / Cartera Improductiva',

  // Asset Quality (C component - Calidad de Activos)
  'C1': 'Activo Productivo / Activo Total',
  'C2': 'Morosidad',
  'C3': 'Cobertura de Cartera Improductiva',
  'C4': 'Cobertura de Cartera en Riesgo',

  // Liquidity (L component)
  'L1': 'Ratio de Inversiones Líquidas',
  'L2': 'Fondos Disponibles / Total Depósitos',
  'L3': 'Liquidez Estructural',
  'L4': 'Cobertura 25 Mayores Depositantes',
  'L5': 'Cobertura 100 Mayores Depositantes',

  // Efficiency & Management (EG component - Eficiencia y Gestión)
  'EG1': 'Grado de Absorción',
  'EG2': 'Gastos Operativos / Activo Total Promedio',
  'EG3': 'Gastos de Personal / Activo Total Promedio',
  'EG4': 'Gastos Operativos / Margen Financiero',

  // Profitability (R component - Rentabilidad)
  'R1': 'ROA (Rendimiento sobre Activos)',
  'R2': 'ROE (Rendimiento sobre Patrimonio)',
  'R3': 'Margen Financiero Neto',
  'R4': 'Margen de Intermediación',

  // Interest Rate Sensitivity (STI component - Sensibilidad)
  'STI1': 'Duración de Activos',
  'STI2': 'Duración de Pasivos',
  'STI3': 'Gap de Duración',
};

// ============================================================================
// CAMEL Component Names
// ============================================================================

export const COMPONENT_NAMES_ES: Record<string, string> = {
  'C': 'Calidad de Activos',
  'S': 'Solvencia',
  'L': 'Liquidez',
  'EG': 'Eficiencia y Gestión',
  'R': 'Rentabilidad',
  'STI': 'Sensibilidad a Tasa de Interés',
};

// ============================================================================
// Period Format (YYYY-MM → mmm-yy Spanish format)
// ============================================================================

const MONTH_NAMES_ES: Record<string, string> = {
  '01': 'ene',
  '02': 'feb',
  '03': 'mar',
  '04': 'abr',
  '05': 'may',
  '06': 'jun',
  '07': 'jul',
  '08': 'ago',
  '09': 'sep',
  '10': 'oct',
  '11': 'nov',
  '12': 'dic',
};

/**
 * Convert BigQuery period format (YYYY-MM) to Spanish report format (mmm-yy)
 * @example formatPeriodES('2024-03') → 'mar-24'
 */
export function formatPeriodES(period: string): string {
  const [year, month] = period.split('-');
  const monthName = MONTH_NAMES_ES[month] || month;
  const yearShort = year.slice(2);
  return `${monthName}-${yearShort}`;
}

/**
 * Convert Spanish period format (mmm-yy) to BigQuery format (YYYY-MM)
 * @example parsePeriodES('mar-24') → '2024-03'
 */
export function parsePeriodES(periodES: string): string {
  const [monthName, yearShort] = periodES.split('-');
  const month = Object.entries(MONTH_NAMES_ES).find(([_, name]) => name === monthName)?.[0] || '01';
  const year = `20${yearShort}`;
  return `${year}-${month}`;
}

// ============================================================================
// Report Section Titles (Spanish)
// ============================================================================

export const REPORT_SECTIONS_ES = {
  metadata: 'Información General',
  riesgo_entidad: 'Riesgo de la Entidad',
  cuadro_03: 'Principales elementos de apetito al riesgo',
  estructura_balance: 'Estructura de Balance',
  cuadro_04: 'Evolución Balance USD millones',
  activo: 'Activo',
  grafico_07: 'Ingresos/Activo Productivo (Promedio)',
  pasivo: 'Pasivo',
  grafico_08: 'Intereses Causados/Activo Productivo (Costo/Activo Productivo)',
  patrimonio: 'Patrimonio',
  grafico_09: 'Margen Financiero Neto',
  indicadores_financieros: 'Indicadores Financieros',
  cuadro_05: 'Indicadores Financieros',
  solvencia: 'Solvencia',
  grafico_10: 'Indicadores de Solvencia',
  liquidez: 'Liquidez',
  grafico_11: 'Indicadores de Liquidez',
  calidad_activos: 'Calidad de Activos',
  grafico_12: 'Indicadores de Calidad de Activos',
  eficiencia_gestion: 'Eficiencia y Gestión',
  grafico_13: 'Indicadores de Eficiencia',
  rentabilidad: 'Rentabilidad',
  riesgo_interes: 'Riesgo de Tasa de Interés',
  anexo_1: 'Anexo 1: Indicadores Financieros Históricos',
};

// ============================================================================
// Table Column Headers (Spanish)
// ============================================================================

export const TABLE_HEADERS_ES = {
  // Cuadro 03
  cuadro_03: {
    indicador: 'Indicador',
    linea_base: 'Línea Base (%)',
    meta_2024: 'Meta 2024 (%)',
  },

  // Cuadro 04
  cuadro_04: {
    periodo: 'Período',
    activo: 'Activo',
    pasivos: 'Pasivos',
    patrimonio: 'Patrimonio',
  },

  // Cuadro 05
  cuadro_05: {
    indicador: 'Indicador',
    ...Object.fromEntries(
      Array.from({ length: 9 }, (_, i) => [`periodo_${i}`, `Período ${i + 1}`])
    ),
  },

  // Anexo 1
  anexo_1: {
    componente: 'Componente',
    indicador: 'Indicador',
    dic_19: 'dic-19',
    dic_20: 'dic-20',
    dic_21: 'dic-21',
    dic_22: 'dic-22',
    mar_23: 'mar-23',
    jun_23: 'jun-23',
    sep_23: 'sep-23',
    dic_23: 'dic-23',
    mar_24: 'mar-24',
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Translate a BigQuery field name to Spanish
 */
export function translateField(field: string): string {
  return FIELD_TRANSLATIONS[field] || field;
}

/**
 * Get Spanish name for financial indicator
 */
export function translateIndicator(indicatorId: string): string {
  return INDICATOR_NAMES_ES[indicatorId] || indicatorId;
}

/**
 * Get Spanish name for CAMEL component
 */
export function translateComponent(componentCode: string): string {
  return COMPONENT_NAMES_ES[componentCode] || componentCode;
}

/**
 * Format currency value in Spanish format
 * @example formatCurrencyES(1234567.89) → '1.234.567,89'
 */
export function formatCurrencyES(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format percentage value in Spanish format
 * @example formatPercentageES(12.345) → '12,35%'
 */
export function formatPercentageES(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'percent',
  }).format(value / 100);
}

/**
 * Format number in Spanish format (thousands separator)
 * @example formatNumberES(1234567) → '1.234.567'
 */
export function formatNumberES(value: number): string {
  return new Intl.NumberFormat('es-EC').format(value);
}

/**
 * Format millions value in Spanish format
 * @example formatMillionsES(259.75) → '259,75'
 */
export function formatMillionsES(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
